const KeenSlider = window.KeenSlider
const { setInitialised } = window.BAO.Carousel.plugins

const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let timeout
    let mouseOver = false
    let startTime = null
    let remaining = delay

    const clearCurrentTimeout = () => {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
    }

    const setNextTimeout = () => {
      clearCurrentTimeout()
      if (mouseOver) return
      startTime = Date.now()
      timeout = setTimeout(() => {
        carousel.next()
        remaining = delay
        setNextTimeout()
      }, remaining)
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', () => {
          if (!mouseOver) {
            mouseOver = true
            if (timeout && startTime) {
              remaining -= Date.now() - startTime
              clearCurrentTimeout()
            }
          }
        })
        carousel.container.addEventListener('mouseout', () => {
          if (mouseOver) {
            mouseOver = false
            startTime = Date.now()
            timeout = setTimeout(() => {
              carousel.next()
              remaining = delay
              setNextTimeout()
            }, remaining)
          }
        })
      }
      remaining = delay
      setNextTimeout()
    })

    carousel.on('dragStarted', () => {
      if (timeout && startTime) {
        remaining -= Date.now() - startTime
        clearCurrentTimeout()
      }
    })
    carousel.on('animationEnded', () => {
      remaining = delay
      setNextTimeout()
    })
    carousel.on('updated', () => {
      remaining = delay
      setNextTimeout()
    })
  }
}

const useProgress = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let startTime = null
    let animationFrame = null
    let mouseOver = false
    let remaining = delay

    const progressEl = document.querySelector('[data-brand-carousel-el="progress"]')

    const clearCurrentAnimation = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
        animationFrame = null
      }
    }

    const animateProgress = (timestamp) => {
      if (mouseOver) return
      if (!startTime) startTime = timestamp

      const elapsed = timestamp - startTime
      const progress = Math.min(((delay - remaining + elapsed) / delay) * 100, 100)
      if (progressEl) {
        progressEl.style.setProperty('--Progress', `${progress}%`)
      }
      if (elapsed < remaining) {
        animationFrame = requestAnimationFrame(animateProgress)
      }
    }

    carousel.on('created', () => {
      if (pauseOnHover && progressEl) {
        carousel.container.addEventListener('mouseover', () => {
          if (!mouseOver) {
            mouseOver = true
            if (animationFrame && startTime) {
              remaining -= performance.now() - startTime
              clearCurrentAnimation()
            }
          }
        })
        carousel.container.addEventListener('mouseout', () => {
          if (mouseOver) {
            mouseOver = false
            startTime = performance.now()
            animationFrame = requestAnimationFrame(animateProgress)
          }
        })
      }
      remaining = delay
      startTime = performance.now()
      clearCurrentAnimation()
      animationFrame = requestAnimationFrame(animateProgress)
    })

    carousel.on('dragStarted', () => {
      if (animationFrame && startTime) {
        remaining -= performance.now() - startTime
        clearCurrentAnimation()
      }
    })
    carousel.on('animationEnded', () => {
      remaining = delay
      startTime = performance.now()
      clearCurrentAnimation()
      animationFrame = requestAnimationFrame(animateProgress)
    })
    carousel.on('updated', () => {
      remaining = delay
      startTime = performance.now()
      clearCurrentAnimation()
      animationFrame = requestAnimationFrame(animateProgress)
    })
  }
}

class BrandCarousel extends HTMLElement {
  connectedCallback () {
    this.quoteEl = this.querySelector('[data-brand-carousel-el="quote"]')
    this.slidesEl = this.querySelector('[data-brand-carousel-el="slides"]')
    this.slideEls = this.querySelectorAll('[data-brand-carousel-el="slide"]')
    this.progressEl = this.querySelector('[data-brand-carousel-el="progress"]')

    this.carousel = this.setupCarousel()

    this.setupListeners()
  }

  setupListeners () {
    this.slideEls.forEach(slide => {
      slide.addEventListener('click', () => this.moveToSlide(slide))
    })
  }

  setupCarousel () {
    return new KeenSlider(
      this.slidesEl,
      {
        selector: '[data-brand-carousel-el="slide"]',
        defaultAnimation: {
          duration: 1500,
        },
        loop: true,
        speed: 1000,
        mode: 'snap',
        slides: {
          perView: 1,
          origin: 'center',
        },
        breakpoints: {
          '(min-width: 750px)': {
            slides: {
              perView: 5,
              spacing: 70,
            },
          },
          '(min-width: 1024px)': {
            slides: {
              perView: 6,
              spacing: 70,
            },
          },
        },
        slideChanged: instance => {
          this.updateQuote(instance)
          this.setActiveSlide(instance)

        }
      },
      [
        setInitialised(),
        useAutoplay(Number(this.dataset.autoplayInterval), { pauseOnHover: true }),
        useProgress(Number(this.dataset.autoplayInterval), { pauseOnHover: true }),
      ],
    )
  }

  moveToSlide (slide) {
    const index = Array.from(this.slideEls).indexOf(slide)
    this.carousel.moveToIdx(index)
  }

  updateQuote (instance) {
    const currentSlide = instance.track.details.rel
    const quote = this.slideEls[currentSlide].getAttribute('data-quote')

    if (!this.quoteEl && !quote) return

    this.quoteEl.textContent = `“${quote}”`
  }

  setActiveSlide (instance) {
    this.activeSlide = instance.track.details.rel

    this.slideEls.forEach((slide, i) => {
      slide.dataset.activeSlide = i === this.activeSlide ? 'true' : 'false'
    })
  }

  get activeSlide () {
    return this._activeSlide
  }

  set activeSlide (val) {
    this._activeSlide = val
  }
}

if (!customElements.get('brand-carousel')) {
  customElements.define('brand-carousel', BrandCarousel)
}
